/**
 * Template Storage Service
 * 
 * This service handles saving and retrieving templates from local storage.
 * It will be replaced with a database implementation in the future.
 */

import { generateDefaultTemplates } from './TemplateExtractor';

// Storage key for templates in local storage
const STORAGE_KEY = 'palmheist_scratch_card_templates';

/**
 * Initialize the template storage with default templates if empty
 */
export function initializeTemplateStorage() {
  const templates = getTemplatesFromLocalStorage();
  
  // If no templates exist, populate with defaults
  if (!templates || templates.length === 0) {
    const defaultTemplates = generateDefaultTemplates();
    saveTemplatesToLocalStorage(defaultTemplates);
    console.log('Template storage initialized with default templates');
    return defaultTemplates;
  }
  
  return templates;
}

/**
 * Get all templates from local storage
 * @returns {Array} Array of template objects
 */
export function getTemplatesFromLocalStorage() {
  try {
    const templatesJson = localStorage.getItem(STORAGE_KEY);
    return templatesJson ? JSON.parse(templatesJson) : [];
  } catch (error) {
    console.error('Error retrieving templates from local storage:', error);
    return [];
  }
}

/**
 * Save multiple templates to local storage
 * @param {Array} templates - Array of template objects to save
 */
export function saveTemplatesToLocalStorage(templates) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
  } catch (error) {
    console.error('Error saving templates to local storage:', error);
  }
}

/**
 * Save a single template to local storage
 * @param {Object} template - Template object to save
 * @returns {Object} The saved template
 */
export function saveTemplateToLocalStorage(template) {
  try {
    const templates = getTemplatesFromLocalStorage();
    const existingIndex = templates.findIndex(t => t.id === template.id);
    
    if (existingIndex >= 0) {
      // Update existing template
      templates[existingIndex] = {
        ...template,
        updatedAt: new Date()
      };
    } else {
      // Add new template
      templates.push({
        ...template,
        id: template.id || `template-${Date.now()}`,
        createdAt: new Date(),
        updatedAt: new Date()
      });
    }
    
    saveTemplatesToLocalStorage(templates);
    return template;
  } catch (error) {
    console.error('Error saving template to local storage:', error);
    return template;
  }
}

/**
 * Get a template by ID from local storage
 * @param {string} id - Template ID
 * @returns {Object|null} The template object or null if not found
 */
export function getTemplateById(id) {
  const templates = getTemplatesFromLocalStorage();
  return templates.find(template => template.id === id) || null;
}

/**
 * Delete a template from local storage
 * @param {string} id - Template ID to delete
 * @returns {boolean} True if successful, false otherwise
 */
export function deleteTemplate(id) {
  try {
    const templates = getTemplatesFromLocalStorage();
    const filteredTemplates = templates.filter(template => template.id !== id);
    
    if (filteredTemplates.length !== templates.length) {
      saveTemplatesToLocalStorage(filteredTemplates);
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error deleting template from local storage:', error);
    return false;
  }
}

/**
 * Get templates by tags
 * @param {Array} tags - Array of tags to filter by
 * @returns {Array} Array of matching templates
 */
export function getTemplatesByTags(tags) {
  const templates = getTemplatesFromLocalStorage();
  
  if (!tags || tags.length === 0) {
    return templates;
  }
  
  return templates.filter(template => {
    return tags.some(tag => template.tags.includes(tag));
  });
}

/**
 * Duplicate a template
 * @param {string} id - Template ID to duplicate
 * @returns {Object|null} The duplicated template or null if original not found
 */
export function duplicateTemplate(id) {
  const template = getTemplateById(id);
  
  if (!template) {
    return null;
  }
  
  const duplicatedTemplate = {
    ...template,
    id: `template-${Date.now()}`,
    name: `${template.name} (Copy)`,
    createdAt: new Date(),
    updatedAt: new Date()
  };
  
  saveTemplateToLocalStorage(duplicatedTemplate);
  return duplicatedTemplate;
}
